import PropTypes from 'prop-types';
import React from 'react';
import { useTranslations } from 'next-intl';
import { isPeriod } from '../../../helper';

import StyledEventInfo from './eventInfo.style';
import MixedCopy from '../mixed-copy/mixedCopy';
import EventDate from '../event-date/eventDate';
import Copy from '../../atoms/copy/copy';

const EventInfo = ({
  section,
  location,
  additionalInformation,
  eventPrices = {
    prices: null,
    copy: null
  },
  eventType,
  gomusTourCategory,
  gomusTourCategoryOverride,
  gomusEventLanguages,
  gomusEventCategory,
  gomusEventCategoryOverride,
  gomusLocation,
  gomusLanguage,
  gomusLanguages,
  duration
}) => {
  const t = useTranslations();
  const gomusDates = section !== 'gomusTours' && section !== 'gomusEvents';
  const { prices, copy } = eventPrices;
  const { type, startTime, startDate, endTime, endDate } = eventType;

  const start = type === 'single' ? startTime : startDate;
  const end = type === 'single' ? endTime : endDate;

  const formattedStartTime = gomusDates
    ? new Date(start.c).toLocaleTimeString('de', {
        timeStyle: 'short'
      })
    : null;

  const formattedEndTime = gomusDates
    ? new Date(end?.c).toLocaleTimeString('de', {
        timeStyle: 'short'
      })
    : null;

  const period = gomusDates ? isPeriod(start, end) : false;
  const hasTime = gomusDates && start.H && start.i && end.H && end.i;

  let title = '';

  const gomusEventCategoryTitle =
    gomusEventCategoryOverride?.at(0)?.title || gomusEventCategory?.at(0)?.title;

  const gomusTourCategoryTitle =
    gomusTourCategoryOverride?.at(0)?.title || gomusTourCategory?.at(0)?.title;

  let languages = gomusLanguage || gomusLanguages;

  // NOTE: FR-1740 describes that if a gomusDate has not languages set, it should display the languages of its correspondings gomusEvent.
  if (!languages || (languages?.length === 0 && section === 'gomusDates')) {
    languages = gomusEventLanguages;
  }

  if (section === 'gomusEvents') {
    return null;
  }

  if (section === 'gomusTours') {
    title = gomusTourCategoryTitle;
  } else {
    title = gomusEventCategoryTitle;
  }

  return (
    <StyledEventInfo $period={period} $gomusDates={gomusDates}>
      {section !== 'gomusTours' && (
        <EventDate startDate={start} endDate={end} period={period} className="event__date" />
      )}

      <div className="event__content-wrapper">
        <div className="event__content">
          {hasTime && (
            <time className="event__time">
              {formattedStartTime} – {formattedEndTime}
            </time>
          )}

          <p className="event__location">{title}</p>

          {prices && (
            <ul className="event__price-list">
              {prices.map(price => (
                <li key={price.category}>
                  <p className="event__price-name">{price.category}</p>
                  <p className="event__price-value">{price.price}</p>
                </li>
              ))}
            </ul>
          )}
          {copy && <p className="event__copy">{copy}</p>}

          {(languages || duration || location || gomusLocation) && (
            <div className="event__meta">
              <Copy smallThick className="event__meta-headline">
                {t('eventInfo.meta')}
              </Copy>

              <ul className="event__meta-list">
                <li>
                  <Copy smallThick className="event__meta-name" tag="span">
                    {t('eventInfo.time')}
                  </Copy>
                  <Copy small tag="span">
                    {section === 'gomusTours' ? (
                      t('eventInfo.requestTime')
                    ) : (
                      <>{formattedStartTime}</>
                    )}
                  </Copy>
                </li>
                {duration && (
                  <li>
                    <Copy smallThick className="event__meta-name" tag="span">
                      {t('eventInfo.duration')}
                    </Copy>
                    <Copy small tag="span">
                      {duration}
                    </Copy>
                  </li>
                )}
                {languages && languages.length > 0 && (
                  <li>
                    <Copy smallThick className="event__meta-name" tag="span">
                      {t('eventInfo.gomusLanguage')}
                    </Copy>
                    <Copy small tag="span">
                      {languages.map((language, index) =>
                        languages.length !== index + 1 ? `${language.title}, ` : language.title
                      )}
                    </Copy>
                  </li>
                )}

                {gomusLocation && (
                  <li>
                    <Copy smallThick className="event__meta-name" tag="span">
                      {t('eventInfo.gomusLocation')}
                    </Copy>
                    <Copy small tag="span">
                      {location && location.title}
                      {gomusLocation && gomusLocation.title}
                    </Copy>
                  </li>
                )}

                {location && (
                  <li>
                    <Copy smallThick className="event__meta-name" tag="span">
                      {t('eventInfo.gomusLocation')}
                    </Copy>
                    <Copy small tag="span">
                      {location.title}
                    </Copy>
                  </li>
                )}
              </ul>
            </div>
          )}

          {additionalInformation && (
            <MixedCopy
              html={additionalInformation}
              fullWidth
              small
              className="event__information"
            />
          )}
        </div>
      </div>
    </StyledEventInfo>
  );
};

EventInfo.propTypes = {
  section: PropTypes.string.isRequired,
  eventType: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.shape({
        c: PropTypes.string,
        d: PropTypes.string,
        m: PropTypes.string,
        Y: PropTypes.string,
        H: PropTypes.string,
        i: PropTypes.string
      }),
      endDate: PropTypes.shape({
        c: PropTypes.string,
        d: PropTypes.string,
        m: PropTypes.string,
        Y: PropTypes.string,
        H: PropTypes.string,
        i: PropTypes.string
      }),
      startTime: PropTypes.shape({
        c: PropTypes.string,
        d: PropTypes.string,
        m: PropTypes.string,
        Y: PropTypes.string,
        H: PropTypes.string,
        i: PropTypes.string
      }),
      endTime: PropTypes.shape({
        c: PropTypes.string,
        d: PropTypes.string,
        m: PropTypes.string,
        Y: PropTypes.string,
        H: PropTypes.string,
        i: PropTypes.string
      })
    })
  ).isRequired,
  eventPrices: PropTypes.shape({
    copy: PropTypes.string,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    )
  }),
  gomusTourCategory: PropTypes.arrayOf(PropTypes.shape()),
  gomusEventLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string
    })
  ),
  gomusEventCategory: PropTypes.arrayOf(PropTypes.shape()),
  location: PropTypes.shape({
    title: PropTypes.string.isRequired
  }),
  gomusLocation: PropTypes.shape({
    title: PropTypes.string.isRequired
  }),
  gomusLanguage: PropTypes.arrayOf(PropTypes.shape()),
  gomusLanguages: PropTypes.arrayOf(PropTypes.shape()),
  additionalInformation: PropTypes.string,
  duration: PropTypes.string
};

export default EventInfo;
